import { useCallback, useState } from 'react';
import { sanitizeUserMessageForDisplay } from '../constants/canvasRules';
import {
  ConversationTab,
  DEFAULT_SEARCH_CONTEXT_TYPE,
  SearchContextType,
} from '../types/conversation';
import { secureStorage } from '../utils/secureStorage';

const DEFAULT_LLM_MODEL = 'gemini/gemini-3.1-pro-preview';
export const TAB_MESSAGE_CACHE_LIMIT = 30;

const normalizeSearchContextType = (value: unknown): SearchContextType => {
  if (value === 'global' || value === 'specific' || value === 'web') {
    return value;
  }
  return DEFAULT_SEARCH_CONTEXT_TYPE;
};

const resolveStoredSearchContextType = (tab: Partial<ConversationTab>): SearchContextType => {
  const normalizedType = normalizeSearchContextType(tab.searchContextType);
  const hasExplicitSelection = tab.hasExplicitSearchContextSelection === true;
  if (!hasExplicitSelection && normalizedType === 'global') {
    return DEFAULT_SEARCH_CONTEXT_TYPE;
  }
  return normalizedType;
};

const sanitizeConversationMessageForDisplay = (message: any) => {
  if (message?.role !== 'user') {
    return message;
  }
  return {
    ...message,
    content: sanitizeUserMessageForDisplay(
      typeof message.content === 'string' ? message.content : String(message.content || '')
    ),
  };
};

export const sanitizeConversationTabForStorage = (tab: ConversationTab) => ({
  ...tab,
  messages: (tab.messages || []).slice(-TAB_MESSAGE_CACHE_LIMIT).map(message => {
    const sanitizedMessage = sanitizeConversationMessageForDisplay(message);
    return {
      ...sanitizedMessage,
      attachments: undefined,
      timestamp: (() => {
        const dateValue =
          sanitizedMessage.timestamp instanceof Date
            ? sanitizedMessage.timestamp
            : new Date(sanitizedMessage.timestamp);
        return Number.isNaN(dateValue.getTime())
          ? new Date().toISOString()
          : dateValue.toISOString();
      })(),
      isStreaming: false,
      currentStatus: undefined,
      streamUpdatedAt: undefined,
    };
  }),
  draftMessage: tab.draftMessage || '',
});

export const normalizeConversationTabFromStorage = (
  tab: ConversationTab
): ConversationTab => ({
  ...tab,
  backendConversationId: tab.backendConversationId ?? null,
  searchContextType: resolveStoredSearchContextType(tab),
  hasExplicitSearchContextSelection: tab.hasExplicitSearchContextSelection === true,
  useLiveData: tab.useLiveData || false,
  selectedLlmModel: tab.selectedLlmModel || DEFAULT_LLM_MODEL,
  draftMessage: tab.draftMessage || '',
  useCanvas: tab.useCanvas || false,
  selectedKnowledgeSources: tab.selectedKnowledgeSources || [],
  selectedConnectorSources: tab.selectedConnectorSources || [],
  selectedCrewId: tab.selectedCrewId ?? null,
  sessionId: tab.sessionId ?? null,
  ragConfig: tab.ragConfig || {
    rag_top_k: 100,
    rag_similarity_threshold: 0.4,
    rag_tags: [],
    rag_max_context_length: 50000,
  },
  messages: (tab.messages || []).map((msg: any) => {
    const sanitizedMessage = sanitizeConversationMessageForDisplay(msg);
    return {
      ...sanitizedMessage,
      timestamp: new Date(sanitizedMessage.timestamp),
    };
  }),
});

export const useConversationTabs = () => {
  const [conversationTabs, setConversationTabs] = useState<ConversationTab[]>(
    []
  );
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const buildDefaultRagConfig = () => ({
    rag_top_k: 100,
    rag_similarity_threshold: 0.4,
    rag_tags: [],
    rag_max_context_length: 50000,
  });

  const saveConversationsToStorage = useCallback(
    (tabs: ConversationTab[], activeId: string | null) => {
      try {
        // Persist tab metadata + short message cache for quick session restore.
        const sanitizedTabs = tabs.map(tab => sanitizeConversationTabForStorage(tab));
        secureStorage.setItem('conversationTabs', JSON.stringify(sanitizedTabs));
        secureStorage.setItem('activeTabId', activeId || '');
      } catch (error) {
        // Ignore storage write errors.
      }
    },
    []
  );

  const loadConversationsFromStorage = useCallback((): {
    tabs: ConversationTab[];
    activeId: string | null;
  } => {
    try {
      const savedTabs = secureStorage.getItem('conversationTabs');
      const savedActiveId = secureStorage.getItem('activeTabId');

      if (savedTabs) {
        const tabs = JSON.parse(savedTabs);
        const processedTabs = tabs.map((tab: ConversationTab) => ({
          ...normalizeConversationTabFromStorage(tab),
          ragConfig: tab.ragConfig || buildDefaultRagConfig(),
        }));

        return {
          tabs: processedTabs || [],
          activeId: savedActiveId || null,
        };
      }
    } catch (error) {
      // Ignore storage read errors and bootstrap with empty state.
    }

    return { tabs: [], activeId: null };
  }, []);

  const createNewConversationTab = useCallback(
    (tabName: string = 'Nova Conversa') => {
      const tabId = `conversation-${Date.now()}`;
      const newTab: ConversationTab = {
        id: tabId,
        name: tabName,
        backendConversationId: null,
        selectedCrewId: null,
        selectedLlmModel: DEFAULT_LLM_MODEL,
        draftMessage: '',
        useCanvas: false,
        messages: [],
        selectedKnowledgeSources: [],
        selectedConnectorSources: [],
        searchContextType: DEFAULT_SEARCH_CONTEXT_TYPE,
        hasExplicitSearchContextSelection: false,
        useLiveData: false,
        sessionId: null,
        // The tab originates locally — no server-side history yet, so the
        // hydration effect must skip the GET /v1/history/session/<id> call.
        createdLocally: true,
        ragConfig: buildDefaultRagConfig(),
      };

      setConversationTabs(prev => {
        const newTabs = [...prev, newTab];
        saveConversationsToStorage(newTabs, tabId);
        return newTabs;
      });
      setActiveTabId(tabId);
      return tabId;
    },
    [saveConversationsToStorage]
  );

  const selectCrewForActiveTab = useCallback(
    (crewId: number, crews: any[]) => {
      if (!activeTabId) return;

      setConversationTabs(prev => {
        const newTabs = prev.map(tab =>
          tab.id === activeTabId
            ? {
                ...tab,
                selectedCrewId: crewId,
                name: crews.find(c => c.id === crewId)?.name || tab.name,
              }
            : tab
        );
        saveConversationsToStorage(newTabs, activeTabId);
        return newTabs;
      });
    },
    [activeTabId, saveConversationsToStorage]
  );

  const closeConversationTab = useCallback(
    (tabId: string) => {
      setConversationTabs(prev => {
        const newTabs = prev.filter(tab => tab.id !== tabId);

        const newActiveId =
          activeTabId === tabId
            ? newTabs.length > 0
              ? newTabs[0].id
              : null
            : activeTabId;
        saveConversationsToStorage(newTabs, newActiveId);
        if (activeTabId === tabId) {
          setActiveTabId(newActiveId);
        }
        return newTabs;
      });
    },
    [activeTabId, saveConversationsToStorage]
  );

  const updateTabMessages = useCallback(
    (tabId: string, newMessages: any[]) => {
      setConversationTabs(prev => {
        const newTabs = prev.map(tab =>
          tab.id === tabId ? { ...tab, messages: newMessages } : tab
        );
        saveConversationsToStorage(newTabs, activeTabId);
        return newTabs;
      });
    },
    [activeTabId, saveConversationsToStorage]
  );

  const updateTabKnowledgeSources = useCallback(
    (tabId: string, sources: any[]) => {
      setConversationTabs(prev => {
        const newTabs = prev.map(tab =>
          tab.id === tabId ? { ...tab, selectedKnowledgeSources: sources } : tab
        );
        saveConversationsToStorage(newTabs, activeTabId);
        return newTabs;
      });
    },
    [activeTabId, saveConversationsToStorage]
  );

  const updateTabConnectorSources = useCallback(
    (tabId: string, sources: any[]) => {
      setConversationTabs(prev => {
        const newTabs = prev.map(tab =>
          tab.id === tabId ? { ...tab, selectedConnectorSources: sources } : tab
        );
        saveConversationsToStorage(newTabs, activeTabId);
        return newTabs;
      });
    },
    [activeTabId, saveConversationsToStorage]
  );

  const updateTabSessionId = useCallback(
    (tabId: string, sessionId: string) => {
      setConversationTabs(prev => {
        const newTabs = prev.map(tab =>
          tab.id === tabId ? { ...tab, sessionId } : tab
        );
        saveConversationsToStorage(newTabs, activeTabId);
        return newTabs;
      });
    },
    [activeTabId, saveConversationsToStorage]
  );

  const updateTabSearchContextType = useCallback(
    (tabId: string, searchContextType: SearchContextType) => {
      setConversationTabs(prev => {
        const newTabs = prev.map(tab =>
          tab.id === tabId
            ? {
                ...tab,
                searchContextType,
                hasExplicitSearchContextSelection: true,
              }
            : tab
        );
        saveConversationsToStorage(newTabs, activeTabId);
        return newTabs;
      });
    },
    [activeTabId, saveConversationsToStorage]
  );

  const updateTabUseLiveData = useCallback(
    (tabId: string, useLiveData: boolean) => {
      setConversationTabs(prev => {
        const newTabs = prev.map(tab =>
          tab.id === tabId ? { ...tab, useLiveData } : tab
        );
        saveConversationsToStorage(newTabs, activeTabId);
        return newTabs;
      });
    },
    [activeTabId, saveConversationsToStorage]
  );

  const updateTabLlmModel = useCallback(
    (tabId: string, selectedLlmModel: string | null) => {
      setConversationTabs(prev => {
        const newTabs = prev.map(tab =>
          tab.id === tabId ? { ...tab, selectedLlmModel } : tab
        );
        saveConversationsToStorage(newTabs, activeTabId);
        return newTabs;
      });
    },
    [activeTabId, saveConversationsToStorage]
  );

  const updateTabDraftMessage = useCallback(
    (tabId: string, draftMessage: string) => {
      setConversationTabs(prev => {
        const newTabs = prev.map(tab =>
          tab.id === tabId ? { ...tab, draftMessage } : tab
        );
        saveConversationsToStorage(newTabs, activeTabId);
        return newTabs;
      });
    },
    [activeTabId, saveConversationsToStorage]
  );

  const updateTabUseCanvas = useCallback(
    (tabId: string, useCanvas: boolean) => {
      setConversationTabs(prev => {
        const newTabs = prev.map(tab =>
          tab.id === tabId ? { ...tab, useCanvas } : tab
        );
        saveConversationsToStorage(newTabs, activeTabId);
        return newTabs;
      });
    },
    [activeTabId, saveConversationsToStorage]
  );

  const createConversationTabFromHistory = useCallback(
    (params: {
      historyConversationId: number;
      tabName: string;
      selectedCrewId?: number | null;
      sessionId?: string | null;
      searchContextType?: SearchContextType;
      useLiveData?: boolean;
    }) => {
      const tabId = `conversation-${params.historyConversationId}-${Date.now()}`;
      const newTab: ConversationTab = {
        id: tabId,
        name: params.tabName,
        backendConversationId: params.historyConversationId,
        selectedCrewId: params.selectedCrewId ?? null,
        selectedLlmModel: DEFAULT_LLM_MODEL,
        draftMessage: '',
        useCanvas: false,
        messages: [],
        selectedKnowledgeSources: [],
        selectedConnectorSources: [],
        searchContextType: normalizeSearchContextType(params.searchContextType),
        hasExplicitSearchContextSelection: Boolean(params.searchContextType),
        useLiveData: params.useLiveData || false,
        sessionId: params.sessionId ?? null,
        ragConfig: buildDefaultRagConfig(),
      };

      setConversationTabs(prev => {
        const newTabs = [...prev, newTab];
        saveConversationsToStorage(newTabs, tabId);
        return newTabs;
      });
      setActiveTabId(tabId);
      return tabId;
    },
    [saveConversationsToStorage]
  );

  const getActiveTab = useCallback(() => {
    return conversationTabs.find(tab => tab.id === activeTabId);
  }, [conversationTabs, activeTabId]);

  const setActiveTab = useCallback((tabId: string) => {
    setActiveTabId(tabId);
  }, []);

  const initializeTabs = useCallback(
    (tabs: ConversationTab[], activeId: string | null) => {
      setConversationTabs(tabs);
      setActiveTabId(activeId);
    },
    []
  );

  return {
    conversationTabs,
    setConversationTabs,
    activeTabId,
    setActiveTabId,
    isInitialized,
    setIsInitialized,
    saveConversationsToStorage,
    loadConversationsFromStorage,
    createNewConversationTab,
    selectCrewForActiveTab,
    closeConversationTab,
    updateTabMessages,
    updateTabKnowledgeSources,
    updateTabConnectorSources,
    updateTabSessionId,
    updateTabSearchContextType,
    updateTabUseLiveData,
    updateTabLlmModel,
    updateTabDraftMessage,
    updateTabUseCanvas,
    createConversationTabFromHistory,
    getActiveTab,
    setActiveTab,
    initializeTabs,
  };
};
